"use client";

import { X, ZoomIn } from "lucide-react";
import { useEffect, useState } from "react";
import w1480 from "@/assets/work-1-480.webp";
import w1768 from "@/assets/work-1-768.webp";
import w1960 from "@/assets/work-1-960.webp";
import w2480 from "@/assets/work-2-480.webp";
import w2768 from "@/assets/work-2-768.webp";
import w2960 from "@/assets/work-2-960.webp";
import w3480 from "@/assets/work-3-480.webp";
import w3768 from "@/assets/work-3-768.webp";
import w3960 from "@/assets/work-3-960.webp";
import w4480 from "@/assets/work-4-480.webp";
import w4768 from "@/assets/work-4-768.webp";
import w4960 from "@/assets/work-4-960.webp";
import w5480 from "@/assets/work-5-480.webp";
import w5768 from "@/assets/work-5-768.webp";
import w5960 from "@/assets/work-5-960.webp";
import w6480 from "@/assets/work-6-480.webp";
import w6768 from "@/assets/work-6-768.webp";
import w6960 from "@/assets/work-6-960.webp";
import { Cta } from "./cta";
import { MaskedHeadline, Reveal } from "./motion-primitives";
import { useQuote } from "./quote-context";
import { ResponsiveImage, srcSet } from "./responsive-image";

const TILE_SIZES = "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw";
const LIGHTBOX_SIZES = "(min-width: 768px) 55vw, 100vw";

const FILTERS = ["All", "Business", "Events", "Recognition", "Gifts", "Promotional"] as const;

type Item = {
  id: string;
  src: string;
  srcSet: string;
  cat: (typeof FILTERS)[number];
  title: string;
  desc: string;
  alt: string;
  category: string;
};

const ITEMS: Item[] = [
  {
    id: "w1",
    src: w1480,
    srcSet: srcSet([
      [w1480, 480],
      [w1768, 768],
      [w1960, 960],
    ]),
    cat: "Business",
    title: "Branded multi-tools",
    desc: "Stainless multi-tools engraved for teams, shops, and client gifts that get used every day.",
    alt: "Stainless steel multi-tools standing upright with circular engraved logos",
    category: "Corporate and Employee Gifts",
  },
  {
    id: "w2",
    src: w2480,
    srcSet: srcSet([
      [w2480, 480],
      [w2768, 768],
      [w2960, 960],
    ]),
    cat: "Promotional",
    title: "Matching corporate tumblers",
    desc: "Insulated tumblers engraved as a consistent set for a company campaign or crew.",
    alt: "Row of dark blue insulated tumblers engraved with a McCullough Roofing logo",
    category: "Branded Promotional Products",
  },
  {
    id: "w3",
    src: w3480,
    srcSet: srcSet([
      [w3480, 480],
      [w3768, 768],
      [w3960, 960],
    ]),
    cat: "Business",
    title: "Hat and tumbler set",
    desc: "A matching snapback and tumbler finished with the same brand mark for a cohesive gift.",
    alt: "Grey and black snapback hat beside a black tumbler with matching Prairie Plains logos",
    category: "Corporate and Employee Gifts",
  },
  {
    id: "w4",
    src: w4480,
    srcSet: srcSet([
      [w4480, 480],
      [w4768, 768],
      [w4960, 960],
    ]),
    cat: "Recognition",
    title: "Custom engraved case",
    desc: "Detailed laser engraving for a keepsake piece that feels personal and lasting.",
    alt: "Red phone case wallet laser-engraved with a dog portrait and farm branding",
    category: "Awards and Recognition",
  },
  {
    id: "w5",
    src: w5480,
    srcSet: srcSet([
      [w5480, 480],
      [w5768, 768],
      [w5960, 960],
    ]),
    cat: "Events",
    title: "Event gear display",
    desc: "Hats and drinkware branded together for outdoor events, crews, and field days.",
    alt: "Custom hats and insulated bottles arranged on a green tractor hood outdoors",
    category: "Event Merchandise",
  },
  {
    id: "w6",
    src: w6480,
    srcSet: srcSet([
      [w6480, 480],
      [w6768, 768],
      [w6960, 960],
    ]),
    cat: "Gifts",
    title: "Personalized tumbler",
    desc: "A one-of-one engraved tumbler finished with a meaningful message or name.",
    alt: "White insulated tumbler engraved with a butterfly and the message Give it to God and go to sleep",
    category: "Personalized Gifts",
  },
];

export function WorkGallery() {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>("All");
  const [active, setActive] = useState<Item | null>(null);
  const { goToQuote } = useQuote();

  const shown = filter === "All" ? ITEMS : ITEMS.filter((i) => i.cat === filter);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active]);

  return (
    <section id="work" className="bg-softwhite py-24 sm:py-32">
      <div className="mx-auto max-w-[1400px] px-5">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <h2 className="font-display text-[clamp(2.2rem,5.5vw,4.5rem)] leading-[0.95] font-bold text-ink">
            <MaskedHeadline lines={["See what", "customization can become."]} />
          </h2>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter projects">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  role="tab"
                  aria-selected={filter === f}
                  onClick={() => setFilter(f)}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold transition ${
                    filter === f
                      ? "border-ink bg-ink text-softwhite"
                      : "border-ink/15 text-ink hover:border-ink/40"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((item) => (
              <button
                key={item.id}
                onClick={() => setActive(item)}
                className="group relative aspect-square overflow-hidden text-left"
                aria-label={`View project: ${item.title}`}
              >
                <ResponsiveImage
                  src={item.src}
                  srcSet={item.srcSet}
                  sizes={TILE_SIZES}
                  alt={item.alt}
                  width={768}
                  height={768}
                  className="size-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute inset-x-0 bottom-0 translate-y-3 p-6 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <p className="eyebrow text-brass-deep">{item.cat}</p>
                  <p className="mt-1.5 font-display text-xl font-semibold text-softwhite">
                    {item.title}
                  </p>
                </div>
                <span className="absolute top-4 right-4 inline-flex size-9 items-center justify-center rounded-full bg-softwhite/90 text-ink opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                  <ZoomIn className="size-4" aria-hidden />
                </span>
              </button>
            ))}
        </div>

        <p className="mt-8 text-xs text-muted-foreground">
          Real project work from the Mills Engraving portfolio.
        </p>
      </div>

      {active && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/90 p-4 backdrop-blur-md"
            onClick={() => setActive(null)}
            role="dialog"
            aria-modal="true"
            aria-label={active.title}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative grid max-h-[90vh] w-full max-w-4xl overflow-auto bg-softwhite md:grid-cols-[1.2fr_1fr]"
            >
              <ResponsiveImage
                src={active.src}
                srcSet={active.srcSet}
                sizes={LIGHTBOX_SIZES}
                alt={active.alt}
                width={960}
                height={960}
                loading="eager"
                className="h-full w-full object-cover"
              />
              <div className="flex flex-col justify-between p-8">
                <div>
                  <p className="eyebrow text-brass-deep">{active.cat}</p>
                  <h3 className="mt-3 font-display text-3xl leading-tight font-semibold text-ink">
                    {active.title}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {active.desc}
                  </p>
                </div>
                <div className="mt-8">
                  <Cta
                    onClick={() => {
                      setActive(null);
                      goToQuote({ category: active.category });
                    }}
                  >
                    Start a Similar Project
                  </Cta>
                </div>
              </div>
              <button
                onClick={() => setActive(null)}
                aria-label="Close"
                className="absolute top-6 right-6 inline-flex size-10 items-center justify-center rounded-full bg-softwhite text-ink"
              >
                <X className="size-5" />
              </button>
            </div>
          </div>
      )}
    </section>
  );
}